import { Model } from './Model';
import { computed, DeepReadonly, readonly, ref, type Ref, toRef } from 'vue';
import { defineStore } from 'pinia';

export interface ISetup<M extends Model, Methods> {
  local?: boolean;
  methods?: Methods & ThisType<IBaseMethods<M> & Methods>;
}

export interface IRepositoryDefinition<M extends Model, Methods> {
  (): TRepository<M, Methods>;
}

export type TRepository<M extends Model, Methods> =
  & IBaseMethods<M>
  & Methods
  ;

interface IBaseMethods<M extends Model> {
  items: Ref<Map<string, M>>;
  isEmpty(): boolean;
  isNotEmpty(): boolean;
  count(): number;
  clear(): void;
  set(items: M[]): void;
  add(items: M[] | M): void;
  all(): M[];
  find(id: string): M | null;
  first(): M | null;
  last(): M | null;
  update(item: M, data?: Partial<M>): void;
  update(item: string, data: Partial<M>): void;
  remove(items: (M | string)[] | M | string): void;
  toArray(): M[];
  getWatchable(): Readonly<Ref<ReadonlyMap<string, DeepReadonly<M>>>>;
}

function createCollection<M extends Model>(): IBaseMethods<M> {
  const items = ref(new Map<string, M>()) as Ref<Map<string, M>>;
  return getBaseMethods(items);
}

function createModelStore<M extends Model>(name: string) {
  return defineStore(name, () => {
    return {
      items: new Map<string, M>(),
    };
  });
}

function getBaseMethods<M extends Model>(items: Ref<Map<string, M>>): IBaseMethods<M> {
  const list = computed(() => Array.from(items.value.values()));

  return {
    items,
    isEmpty(): boolean {
      return this.count() === 0;
    },
    isNotEmpty(): boolean {
      return this.count() > 0;
    },
    count(): number {
      return items.value.size;
    },
    clear(): void {
      items.value.clear();
    },
    set(models: M[]): void {
      this.clear();
      models.forEach((model: M) => {
        items.value.set(getModelId(model), model);
      });
    },
    add(items: M[] | M) {
      if (Array.isArray(items)) {
        items.forEach((item: M) => this.add(item));
      } else {
        this.items.value.set(getModelId(items), items);
      }
    },
    find(id: string): M | null {
      return items.value.get(id) ?? null;
    },
    all(): M[] {
      return list.value;
    },
    first(): M | null {
      return list.value[0] ?? null;
    },
    last(): M | null {
      return list.value[list.value.length - 1] ?? null;
    },
    update(item: string | M, data?: Partial<M>): void {
      if (typeof data !== 'undefined') {
        const id: string = typeof item === 'object'
          ? getModelId(item)
          : item;
        const existingItem = this.find(id);
        if (existingItem) {
          items.value.set(id, existingItem.$update(data));
        }
      } else {
        const model = item as M;
        const id = getModelId(model);
        const existingItem = this.find(id);
        if (existingItem) {
          items.value.set(id, existingItem.$update(model));
        }
      }
    },
    remove(models: (M | string)[] | M | string): void {
      if (Array.isArray(models)) {
        models.forEach((model: M | string) => this.remove(model));
      } else {
        items.value.delete(
          typeof models === 'object'
            ? getModelId(models)
            : models,
        );
      }
    },
    toArray(): M[] {
      return list.value;
    },
    getWatchable(): Readonly<Ref<ReadonlyMap<string, DeepReadonly<M>>>> {
      return readonly(items);
    },
  };
}

function getModelId<M extends Model>(model: M): string {
  const property = (model.constructor as any).primaryKey;
  return model[property as keyof M] as string;
}

export function defineRepository<M extends Model, Methods = {}>(ModelClass: new () => M, setup?: ISetup<M, Methods>): IRepositoryDefinition<M, Methods> {
  let setupMethods = {} as Methods;

  if (setup && setup.methods) {
    setupMethods = setup.methods as Methods;
  }

  if (setup && setup.local) {
    // We must return a named function, so it can be automatically detected in the controller
    return function useRepository() {
      const collection = createCollection<M>();

      return {
        ...collection,
        ...setupMethods,
      };
    };
  }

  const useModelStore = createModelStore<M>(ModelClass.name);

  // We must return a named function, so it can be automatically detected in the controller
  return function useRepository() {
    const store = useModelStore();
    const items = toRef(store.items) as Ref<Map<string, M>>;
    const baseMethods = getBaseMethods(items);

    return {
      ...baseMethods,
      ...setupMethods,
    };
  };
}

export const defineRepositoryExportsForTesting = {
  getModelId,
};
